import Decimal from 'decimal.js';
import { useMemo, useState } from 'react';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { getAssetAllocationColors } from '../../../../../../AdviserSettings/Page/Models/hooks/getAssetAllocationColors';
import { StackedBarData } from '../../../../../../CommonComponents/OPL/charts/StackedBar/StackedBar';
import { AccountTypeOptionsDTO } from '../../../../api/useAccountsOptionsQuery';
import { AccountHoldingDTO, TaxStatusDTO } from '../../../../api/useAccountsQuery';
import { AssetAllocationDTO, AssetClassEnum } from '../../../../api/useDefaultAssetAllocationQuery';
import { getDefaultAssetAllocation } from '../../../../helpers';
import { FormFields } from '../../../AccountPreferences/components/AccountForm/AccountForm.meta';
import { useAccountFormContext } from '../../../AccountPreferences/components/AccountForm/context/useAccountFormContext';
import { useHoldings } from '../../useHoldings';
import { SymbolOption } from './components/SearchSymbolsSelect/useSearchSymbolsSelect';

interface UpsertHoldingModalParams {
  holdingIndex: number;
  holdingsArrayFormBag: UseFieldArrayReturn<FormFields, 'bankAccount.Positions', 'key'>;
  onBaseClose: () => void;
}

export const useUpsertHoldingModal = ({
  holdingIndex,
  holdingsArrayFormBag,
  onBaseClose,
}: UpsertHoldingModalParams) => {
  const formContext = useFormContext<FormFields>();

  const {
    options: {
      AllowAssetBucketingCustomization: allowAssetBucketingCustomization,
      AccountTypeOptions,
      DefaultAssetAllocation,
      AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    },
  } = useAccountFormContext();

  const {
    data: { defaultAssetAllocations },
  } = useHoldings();

  const { fields: holdings, remove, update } = holdingsArrayFormBag;
  const holding = holdings[holdingIndex];

  const [showAddManualSymbol, setShowAddManualSymbol] = useState(!!holding.Id);
  const [selectedSymbolOption, setSelectedSymbolOption] = useState<SymbolOption | null>(null);
  const [showSymbolAllocationWarning, setShowSymbolAllocationWarning] = useState(false);

  const [
    accountType,
    isLinked,
    isIntegrated,
    accountTaxStatus,
    annuityIsAnnuitized,
    watchedHolding,
    price,
    assetAllocation,
    ticker,
    quantity,
  ] = formContext.watch([
    'bankAccount.AccountType',
    'bankAccount.IsLinked',
    'bankAccount.IsIntegrated',
    'bankAccount.TaxStatus',
    'bankAccount.AnnuityIsAnnuitized',
    `bankAccount.Positions.${holdingIndex}`,
    `bankAccount.Positions.${holdingIndex}.Price`,
    `bankAccount.Positions.${holdingIndex}.AssetAllocation`,
    `bankAccount.Positions.${holdingIndex}.Ticker`,
    `bankAccount.Positions.${holdingIndex}.Quantity`,
  ]);

  const { AssetAllocationTaxStatuses: assetAllocationTaxStatusesOptions } = AccountTypeOptions.find(
    option => option.AccountType === accountType,
  ) as AccountTypeOptionsDTO;

  const _defaultAssetAllocation = getDefaultAssetAllocation(DefaultAssetAllocation, {
    AccountType: accountType!,
    TaxStatus: accountTaxStatus!,
    AnnuityIsAnnuitized: annuityIsAnnuitized,
  });
  const taxStatusOptions = assetAllocationTaxStatusesOptions.map(opt => ({
    label: opt.Label,
    value: opt.Value,
  }));

  const controlledAllocationFields = assetAllocation.map((field, index) => {
    return {
      ...field,
      ...(assetAllocation ? assetAllocation[index] : {}),
    };
  }) as AssetAllocationDTO[];

  const [allocationTotal, isAllocationInvalid] = useMemo(() => {
    const total = Object.values(controlledAllocationFields).reduce((acc, currentAssetValue) => {
      if (currentAssetValue.y) {
        return new Decimal(acc).plus(currentAssetValue.y).toNumber();
      }

      return acc;
    }, 0) as number;

    const isInvalid = total !== 100;

    return [total, isInvalid];
  }, [controlledAllocationFields]);

  const allocationColors = getAssetAllocationColors({
    assetAllocation: _defaultAssetAllocation,
  });

  const stackedBarData: StackedBarData = controlledAllocationFields.map((allocation, index) => ({
    label: allocation.name,
    value: allocation.y,
    color: allocationColors[index],
  }));

  const showTaxStatusOption = accountTaxStatus === TaxStatusDTO.Mix && !isLinked && !isIntegrated;

  const onClose = () => {
    if (holding.Id) {
      formContext.resetField(`bankAccount.Positions.${holdingIndex}`);
      return onBaseClose();
    }

    remove(holdingIndex);

    return onBaseClose();
  };

  const onReset = () => {
    if (holding.Id) {
      return formContext.resetField(`bankAccount.Positions.${holdingIndex}.AssetAllocation`);
    }

    return formContext.resetField(`bankAccount.Positions.${holdingIndex}.AssetAllocation`, {
      defaultValue: defaultAssetAllocations,
    });
  };

  const onSave = async () => {
    const validation = await formContext.trigger(`bankAccount.Positions`);

    if (!validation || isAllocationInvalid) {
      return;
    }

    const updatedHolding = {
      ...holding,
      ...watchedHolding,
      IsManuallyCreated: true,
      Id: watchedHolding.Id ?? holding.key,
      AssetClasses: assetAllocation.reduce((acc, asset) => {
        acc[asset.id] = asset.y;

        return acc;
      }, {} as Record<AssetClassEnum, number>),
    };

    update(holdingIndex, updatedHolding);

    const tempHoldings = holdings.map(holdingData => {
      if (holding.key === holdingData.key) {
        return { ...holdingData, ...updatedHolding };
      }
      return holdingData;
    }) as AccountHoldingDTO[];

    const specificPositionsValue = tempHoldings.reduce(
      (acc, { Quantity, Price }) => acc + Quantity * Price,
      0,
    );

    formContext.setValue('bankAccount.SpecificPositionsValue', specificPositionsValue);

    formContext.resetField(`bankAccount.Positions.${holdingIndex}`, {
      defaultValue: updatedHolding,
      keepDirty: true,
      keepTouched: true,
    });

    onBaseClose();
  };

  return {
    data: {
      allocationColors,
      allocationTotal,
      allowAssetBucketingCustomization,
      assetAllocation,
      controlledAllocationFields,
      holding,
      isAllocationInvalid,
      price,
      quantity,
      selectedSymbolOption,
      showAddManualSymbol,
      showSymbolAllocationWarning,
      showTaxStatusOption,
      stackedBarData,
      taxStatusOptions,
      ticker,
      assetBucketingByManagementStyle,
    },
    actions: {
      onClose,
      onReset,
      onSave,
      setSelectedSymbolOption,
      setShowAddManualSymbol,
      setShowSymbolAllocationWarning,
    },
  };
};
